import { Timestamp } from 'firebase/firestore';
import { userTimeZone } from 'src/contexts/identityAndTenancy';
import { toCalendarDate } from 'src/kernel/time';
import { auth as useAuthStore } from 'src/store/auth';
import { computed } from 'vue';

const { DateTime } = require('luxon');

/** @typedef {import('./timeTypes').DateParams} DateParams */
/** @typedef {import('./timeTypes').SingularDateParams} SingularDateParams */
/** @typedef {import('./timeTypes').AdjustDateOptions} AdjustDateOptions */
/** @typedef {import('./timeTypes').AddSubtractDateOptions} AddSubtractDateOptions */

/**
 * This composable implements replacements for quasar's date utils and native js date methods
 * The main purpose of the replacements is adding support for timezones through the use of Luxon. If the date is
 * displayed to the user or the calc performed is affected by timezones (e.g. you need to get "start of date", which is
 * 00:00 and then offset it by a number of hrs, 00:00 will have a different timestamp depending on the timezone) then
 * you should use these timezone-aware methods.
 *
 * The methods follow the naming scheme of methods they replace
 * For quasar date utils the naming is 1:1 (adjustDate -> adjustDate)
 *  examples:
 *  ```javascript
 *  // Quasar's `date`
 *  date.startOfDate(nowStart, 'month')
 *  // useTime composable
 *  this.startOfDate(nowStart, 'month')
 *
 *  // Quasar's `date`
 *  date.adjustDate(d, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
 *  // useTime composable
 *  this.adjustDate(d, { hour: 0, minute: 0, second: 0, millisecond: 0 })
 *  ```
 *
 * For native methods the naming is mostly retained, but some of the methods have 'to' prefix replaced with 'get'
 *  examples:
 *  ```javascript
 *  // native `Date` implementation
 *  new Date(deletedTimestamp).toLocaleDateString()
 *  // useTime composable
 *  this.getLocaleDateString(new Date(deletedTimestamp))
 *
 *  // native `Date` implementation
 *  existingFromTime.getHours()
 *  // useTime composable
 *  this.getHours(existingFromTime)
 *
 *  // native `Date` implementation
 *  ts.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
 *  // useTime composable
 *  this.getLocaleTimeString(ts, { options: { hour: 'numeric', minute: '2-digit' }})
 *  ``````
 *
 * New methods are made to be drop in replacements, so they should behave the same and return the same result if
 * given identical params. These methods do not expose Luxon to the rest of the app, they either return a primitive type
 * or a native Date object.
 *
 */
// eslint-disable-next-line import/prefer-default-export
export function useTime() {
  const auth = useAuthStore();

  const propToLibraryDate = (value) => {
    let vanillaDate = null;
    if (value instanceof Timestamp) {
      vanillaDate = value.toDate();
    } else {
      vanillaDate = new Date(value);
    }

    return DateTime.fromJSDate(vanillaDate).setZone(auth.userDetails?.timeZonePreference);
  };

  /**
   * Returns a date string in a similar format as native .toLocaleDateString() method, can also be provided options to
   * change the format
   *
   * @param {Timestamp|Date|string} date
   * @param {Object=} opts
   * @param {string=} opts.locales
   * @param {Object=} opts.options
   * @return {string}
   */
  const getLocaleDateString = (date, { locales = undefined, options = null } = {}) => {
    let customOptions = options;
    const paramsCheck = ['weekday', 'year', 'month', 'day', 'dateStyle'];
    if (options && paramsCheck.every((param) => !options[param])) {
      customOptions = { year: 'numeric', month: 'numeric', day: 'numeric', ...customOptions };
    }
    return propToLibraryDate(date).setLocale(locales).toLocaleString(customOptions || DateTime.DATE_SHORT);
  };

  /**
   * Returns a date string in a similar format as native .toLocaleTimeString() method, can also be provided options to
   * change the format
   *
   * @param {Timestamp|Date|string} date
   * @param {Object=} opts
   * @param {string=} opts.locales
   * @param {Object=} opts.options
   * @return {string}
   */
  const getLocaleTimeString = (date, { locales = undefined, options = null } = {}) => {
    let customOptions = options;
    const paramsCheck = ['dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits', 'timeStyle'];
    if (options && paramsCheck.every((param) => !options[param])) {
      customOptions = { hour: 'numeric', minute: 'numeric', second: 'numeric', ...customOptions };
    }
    return propToLibraryDate(date).setLocale(locales).toLocaleString(customOptions || DateTime.TIME_WITH_SECONDS);
  };

  /**
   * Returns a date string in a similar format as native .toLocaleString() method, can also be provided options to
   * change the format
   *
   * @param {Timestamp|Date|string} date
   * @param {Object=} opts
   * @param {string=} opts.locales
   * @param {Object=} opts.options
   * @return {string}
   */
  const getLocaleString = (date, { locales = undefined, options = null } = {}) => {
    let customOptions = options;
    const paramsCheck = ['weekday', 'year', 'month', 'day', 'dayPeriod', 'hour', 'minute', 'second',
      'fractionalSecondDigits', 'dateStyle', 'timeStyle'];
    if (options && paramsCheck.every((param) => !options[param])) {
      customOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        ...customOptions,
      };
    }
    return propToLibraryDate(date).setLocale(locales).toLocaleString(customOptions
      || DateTime.DATETIME_SHORT_WITH_SECONDS);
  };

  /**
   * Returns a date string in a similar format as native .toString() method
   *
   * @param {Timestamp|Date|string} date
   * @return {string}
   */
  const getString = (date) => {
    return propToLibraryDate(date).toFormat("ccc MMM dd yyyy HH:mm:ss ZZZZ '('ZZZZZ')'");
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {string=} format
   * @param {string=} locale
   * @return {string}
   */
  const formatDate = (date, format, locale) => {
    return propToLibraryDate(date).setLocale(locale).toFormat(format || "yyyy-MM-dd'T'HH:mm:ss.SSSZZ");
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {string} mask
   * @param {string=} locale
   * @param {boolean=} useDeviceTimezone - ignore user selected timezone
   * @return {Date}
   */
  const extractDate = (date, mask, locale, useDeviceTimezone = false) => {
    const timezoneOverride = useDeviceTimezone ? {} : { zone: auth.userDetails?.timeZonePreference };
    return DateTime.fromFormat(date, mask, { ...timezoneOverride, locale }).toJSDate();
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {AdjustDateOptions} options
   * @return {Date}
   */
  const adjustDate = (date, options) => {
    return propToLibraryDate(date).set(options).toJSDate();
  };

  /**
   * Difference in minutes between device timezone and user selected timezone
   */
  const deviceToTimezoneOffset = computed(() => {
    const deviceOffset = DateTime.now().offset;
    const timezoneOffset = DateTime.now().setZone(auth.userDetails?.timeZonePreference).offset;
    return timezoneOffset - deviceOffset;
  });

  /**
   * IANA timezone string
   */
  const currentIANATimezone = computed(() => {
    return userTimeZone(auth.userDetails, Intl.DateTimeFormat().resolvedOptions().timeZone);
  });

  /**
   * Today's calendar date ('YYYY-MM-DD') in the user's timezone. Reads the clock, so call it from
   * the screen deciding what "today" means, never from a rule that should take the date as input.
   *
   * @return {string}
   */
  const todayCalendarDate = () => toCalendarDate(new Date(), currentIANATimezone.value);

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getDayOfWeek = (date) => {
    return propToLibraryDate(date).weekday;
  };

  /**
   * Returns a day suffix (st, nd, rd, th), only for en locale
   *
   * @param {number} day
   * @return {string}
   */
  const getDaySuffix = (day) => {
    const suffixes = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
    const pluralRules = new Intl.PluralRules('en-US', { type: 'ordinal' });
    return suffixes[pluralRules.select(day)];
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {Object} options
   * @param {boolean} options.zeroBased
   * @return {number}
   */
  const getMonth = (date, { zeroBased = false } = {}) => {
    const { month } = propToLibraryDate(date);
    return zeroBased ? month - 1 : month;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getDate = (date) => {
    return propToLibraryDate(date).day;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {Object} options
   * @param {boolean} options.sundayFirst
   * @param {boolean} options.zeroBased
   * @return {number}
   */
  const getDay = (date, { sundayFirst = true, zeroBased = true } = {}) => {
    let { weekday } = propToLibraryDate(date); // monday = 1, sunday = 7
    if (sundayFirst) {
      weekday += 1;
      if (weekday > 7) {
        weekday = 1;
      }
    }
    if (zeroBased) {
      weekday -= 1;
    }
    return weekday;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getFullYear = (date) => {
    return propToLibraryDate(date).year;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getHours = (date) => {
    return propToLibraryDate(date).hour;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getMinutes = (date) => {
    return propToLibraryDate(date).minute;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getSeconds = (date) => {
    return propToLibraryDate(date).second;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const getMilliseconds = (date) => {
    return propToLibraryDate(date).millisecond;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {number}
   */
  const daysInMonth = (date) => {
    return propToLibraryDate(date).daysInMonth;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {SingularDateParams} unit
   * @return {Date}
   */
  const endOfDate = (date, unit) => {
    return propToLibraryDate(date).endOf(unit).toJSDate();
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {SingularDateParams} unit
   * @return {Date}
   */
  const startOfDate = (date, unit) => {
    return propToLibraryDate(date).startOf(unit).toJSDate();
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {AddSubtractDateOptions} options
   * @return {Date}
   */
  const addToDate = (date, options) => {
    return propToLibraryDate(date).plus(options).toJSDate();
  };

  /**
   * @param {Timestamp|Date|string} date
   * @param {AddSubtractDateOptions} options
   * @return {Date}
   */
  const subtractFromDate = (date, options) => {
    return propToLibraryDate(date).minus(options).toJSDate();
  };

  /**
   * @param {Timestamp|Date|string} date1
   * @param {Timestamp|Date|string} date2
   * @param {DateParams} unit
   * @return {Date}
   */
  const isSameDate = (date1, date2, unit) => {
    return propToLibraryDate(date1).hasSame(propToLibraryDate(date2), unit);
  };

  /**
   * @param {Timestamp|Date|string} date1
   * @param {Timestamp|Date|string} date2
   * @param {DateParams} unit
   * @param {Object} opts
   * @param {'floor'|'round'|'ceil'|'default'} accuracy
   * @return {number}
   */
  const getDateDiff = (date1, date2, unit, opts = {}, accuracy = 'floor') => {
    const dateDiff = propToLibraryDate(date1)
      .diff(propToLibraryDate(date2), unit, { conversionAccuracy: 'longterm', ...opts })
      .as(unit);

    if (accuracy !== 'default') {
      return Math[accuracy](dateDiff);
    }
    return dateDiff;
  };

  /**
   * @param {Timestamp|Date|string} dateTarget
   * @param {Timestamp|Date|string} dateFrom
   * @param {Timestamp|Date|string} dateTo
   * @param {Object} options
   * @param {boolean} options.onlyDate
   * @param {boolean} options.inclusiveFrom
   * @param {boolean} options.inclusiveTo
   * @return {boolean}
   */
  const isBetweenDates = (dateTarget, dateFrom, dateTo, {
    onlyDate = false,
    inclusiveFrom = false,
    inclusiveTo = false,
  }) => {
    const unitParam = onlyDate ? 'day' : 'millisecond';
    const targetLuxon = propToLibraryDate(dateTarget).startOf(unitParam);
    const fromLuxon = propToLibraryDate(dateFrom).startOf(unitParam);
    const toLuxon = propToLibraryDate(dateTo).startOf(unitParam);

    const from = inclusiveFrom ? targetLuxon >= fromLuxon : targetLuxon > fromLuxon;
    const to = inclusiveTo ? targetLuxon <= toLuxon : targetLuxon < toLuxon;

    return from && to;
  };

  /**
   * @param {Timestamp|Date|string} date
   * @return {boolean}
   */
  const isValid = (date) => {
    // pulled the implementation from quasar
    return typeof date === 'number' ? true : Number.isNaN(Date.parse(date)) === false;
  };

  /**
   * The same wall clock, expressed in another timezone: 1pm in the user's timezone becomes 1pm in `customTimezone`.
   * The returned date is **not the same instant**. Used when internal libraries rely on native Date implementation,
   * but we need to display a date as if it was in a different timezone
   *
   * Should be used only if the date is immediately transformed to a string, as while the displayed date is correct, the
   * timestamp will be wrong (e.g. the time is 1pm +0, and we need to make it +2, this method will return 3pm +0),
   * so this should not be used if there are additional date manipulations later
   *
   * Pair it with {@link getTimezoneOffsetDateToLocal} for any value read back out of that library.
   *
   * @param {Timestamp|Date|string} date
   * @param {string=} customTimezone
   * @return {Date}
   */
  const getLocalToTimezoneOffsetDate = (date, customTimezone = 'system') => {
    return propToLibraryDate(date).setZone(customTimezone, { keepLocalTime: true }).toJSDate();
  };

  /**
   * Inverse of {@link getLocalToTimezoneOffsetDate}: reads the `customTimezone` fields of `date` as a wall clock in
   * the user's timezone and returns the instant that denotes. Ambiguous only for a wall clock inside that
   * timezone's own DST gap, which is an hour that never happened there.
   *
   * @param {Date} date
   * @param {string=} customTimezone
   * @return {Date}
   */
  const getTimezoneOffsetDateToLocal = (date, customTimezone = 'system') => {
    return propToLibraryDate(date)
      .setZone(customTimezone)
      .setZone(auth.userDetails?.timeZonePreference, { keepLocalTime: true })
      .toJSDate();
  };

  /**
   * @param {string=} timezone
   * @return {{label: string, gmtLabel: string}}
   */
  const getTimezoneLabels = (timezone = currentIANATimezone.value) => {
    const dt = DateTime.now().setZone(timezone);
    const label = `${dt.toFormat('ZZZZ')} - ${timezone.split('/').pop().replace(/_/g, ' ')}`;
    const gmtLabel = `GMT${dt.toFormat('ZZ')}`;
    const abbreviation = dt.toFormat('ZZZZ');

    return { label, gmtLabel, abbreviation };
  };

  return {
    getLocaleDateString,
    getLocaleTimeString,
    getLocaleString,
    formatDate,
    extractDate,
    adjustDate,
    deviceToTimezoneOffset,
    currentIANATimezone,
    todayCalendarDate,
    getDayOfWeek,
    getDaySuffix,
    getMonth,
    getDate,
    getFullYear,
    getDay,
    getHours,
    getMinutes,
    getSeconds,
    getMilliseconds,
    endOfDate,
    startOfDate,
    addToDate,
    subtractFromDate,
    daysInMonth,
    isSameDate,
    getDateDiff,
    isBetweenDates,
    isValid,
    getString,
    getLocalToTimezoneOffsetDate,
    getTimezoneOffsetDateToLocal,
    getTimezoneLabels,
  };
}
